import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOrderDetails } from '../../services/api/orderService';
import { alpha } from '@mui/material';

// ─── Theme ────────────────────────────────────────────────────────────────────
const C = {
  bg: "#f3de9f",
  navy: "#001f54",
  gold: "#FFD166",
  navyStrong: "#001133",
  navyMid: alpha("#001f54", 0.75),
  navyLight: alpha("#001f54", 0.45),
  navyGhost: alpha("#001f54", 0.12),
};

const THEME = {
  gold:       C.gold,           // '#FFD166' - Main gold accent
  goldDark:   '#e6b800',        // Slightly darker gold for gradients
  goldDeep:   '#ccaa00',        // Deeper gold for accents
  goldLight:  alpha(C.gold, 0.3),  // Light gold background
  goldFaint:  alpha(C.gold, 0.08), // Faint gold background
  white:      '#ffffff',
  navy:       C.navy,           // '#001f54' - Navy blue primary
  navyStrong: C.navyStrong,     // '#001133' - Darker navy
  navyMid:    C.navyMid,        // Navy with 0.75 opacity
  navyLight:  C.navyLight,      // Navy with 0.45 opacity
  navyGhost:  C.navyGhost,      // Navy with 0.12 opacity
  textPrimary: C.navyStrong,    // Dark navy for primary text
  textMuted:  C.navyMid,        // Navy with opacity for muted text
  border:     alpha(C.gold, 0.3), // Gold-tinted border
  shadow:     `0 24px 64px ${alpha(C.navy, 0.15)}, 0 4px 16px ${alpha(C.navy, 0.08)}`,
};

// ─── Keyframe injection ────────────────────────────────────────────────────────
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=DM+Sans:wght@400;500;600&display=swap');

@keyframes osp-popIn {
  0%   { opacity:0; transform:scale(0.65) translateY(28px); }
  70%  { transform:scale(1.03) translateY(-4px); }
  100% { opacity:1; transform:scale(1) translateY(0); }
}
@keyframes osp-checkDraw {
  from { stroke-dashoffset: 40; }
  to   { stroke-dashoffset: 0; }
}
@keyframes osp-circlePop {
  0%   { transform:scale(0); opacity:0; }
  60%  { transform:scale(1.12); opacity:1; }
  100% { transform:scale(1); opacity:1; }
}
@keyframes osp-pulse {
  0%   { transform:scale(1); opacity:0.7; }
  100% { transform:scale(1.9); opacity:0; }
}
@keyframes osp-slideUp {
  from { opacity:0; transform:translateY(14px); }
  to   { opacity:1; transform:translateY(0); }
}
@keyframes osp-shimmer {
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
}
@keyframes osp-spin {
  to { transform: rotate(360deg); }
}
@keyframes osp-badgePop {
  0%   { opacity:0; transform:scale(0.5) translateY(6px); }
  70%  { transform:scale(1.08); }
  100% { opacity:1; transform:scale(1) translateY(0); }
}
@keyframes osp-ringPulse {
  0%   { transform:scale(1); opacity:0.5; }
  100% { transform:scale(2.2); opacity:0; }
}
`;

function injectStyles() {
  if (document.getElementById('osp-styles')) return;
  const el = document.createElement('style');
  el.id = 'osp-styles';
  el.textContent = CSS;
  document.head.appendChild(el);
}

// ─── Sparkle Canvas ────────────────────────────────────────────────────────────
function SparkleCanvas({ trigger }) {
  const canvasRef = useRef(null);
  const rafRef    = useRef(null);

  useEffect(() => {
    if (!trigger) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    canvas.width  = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    const cx = canvas.width  / 2;
    const cy = canvas.height * 0.32;

    const colors = [
      THEME.gold, THEME.goldDark, '#fff', alpha(THEME.gold, 0.7),
      THEME.gold, '#f0d48a', '#f5deb3', '#fff8dc',
    ];

    const particles = Array.from({ length: 72 }, () => {
      const angle = Math.random() * Math.PI * 2;
      const speed = 1.8 + Math.random() * 4;
      return {
        x: cx, y: cy,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 2.2,
        size:    2.5 + Math.random() * 5.5,
        color:   colors[Math.floor(Math.random() * colors.length)],
        alpha:   1,
        decay:   0.011 + Math.random() * 0.014,
        gravity: 0.055 + Math.random() * 0.04,
        shape:   Math.random() > 0.45 ? 'star' : 'circle',
        rot:     Math.random() * Math.PI * 2,
        rotSpd:  (Math.random() - 0.5) * 0.18,
      };
    });

    function drawStar(c, x, y, r, rot) {
      c.save();
      c.translate(x, y);
      c.rotate(rot);
      c.beginPath();
      for (let i = 0; i < 5; i++) {
        const a = (i * 4 * Math.PI) / 5 - Math.PI / 2;
        const b = a + (2 * Math.PI) / 5;
        if (i === 0) c.moveTo(Math.cos(a) * r, Math.sin(a) * r);
        else         c.lineTo(Math.cos(a) * r, Math.sin(a) * r);
        c.lineTo(Math.cos(b) * r * 0.42, Math.sin(b) * r * 0.42);
      }
      c.closePath();
      c.fill();
      c.restore();
    }

    function loop() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      let alive = false;
      for (const p of particles) {
        if (p.alpha <= 0) continue;
        alive = true;
        p.x  += p.vx;
        p.y  += p.vy;
        p.vy += p.gravity;
        p.alpha -= p.decay;
        p.rot   += p.rotSpd;
        ctx.globalAlpha = Math.max(0, p.alpha);
        ctx.fillStyle   = p.color;
        if (p.shape === 'star') drawStar(ctx, p.x, p.y, p.size, p.rot);
        else {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;
      if (alive) rafRef.current = requestAnimationFrame(loop);
    }

    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [trigger]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position:       'absolute',
        inset:          0,
        width:          '100%',
        height:         '100%',
        pointerEvents:  'none',
        zIndex:         0,
      }}
    />
  );
}

// ─── Animated Checkmark ────────────────────────────────────────────────────────
function AnimatedCheck({ visible }) {
  return (
    <div style={{
      width:          80,
      height:         80,
      borderRadius:   '50%',
      background:     THEME.white,
      display:        'flex',
      alignItems:     'center',
      justifyContent: 'center',
      margin:         '0 auto 14px',
      position:       'relative',
      boxShadow:      `0 0 0 12px ${alpha(THEME.gold, 0.18)}`,
      animation:      visible ? 'osp-circlePop 0.5s cubic-bezier(.34,1.56,.64,1) 0.3s both' : 'none',
    }}>
      {/* pulse ring */}
      <span style={{
        position:     'absolute',
        inset:        -6,
        borderRadius: '50%',
        border:       `2px solid ${THEME.gold}`,
        animation:    visible ? 'osp-ringPulse 0.8s ease-out 0.85s both' : 'none',
      }} />

      <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
        <circle cx="20" cy="20" r="20" fill={THEME.gold} />
        <polyline
          points="10,20 16,28 30,12"
          stroke={THEME.white}
          strokeWidth="3.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          strokeDasharray="40"
          strokeDashoffset="0"
          style={{
            animation: visible
              ? 'osp-checkDraw 0.45s ease-out 0.75s both'
              : 'none',
            strokeDashoffset: visible ? undefined : 40,
          }}
        />
      </svg>
    </div>
  );
}

// ─── Loading State ─────────────────────────────────────────────────────────────
function LoadingState() {
  return (
    <div style={{
      display:        'flex',
      flexDirection:  'column',
      alignItems:     'center',
      justifyContent: 'center',
      padding:        40,
      gap:            14,
    }}>
      <div style={{
        width:        36,
        height:       36,
        borderRadius: '50%',
        border:       `3px solid ${THEME.goldLight}`,
        borderTop:    `3px solid ${THEME.gold}`,
        animation:    'osp-spin 0.8s linear infinite',
      }} />
      <span style={{ fontSize: 14, color: THEME.textMuted, fontFamily: "'DM Sans', sans-serif" }}>
        Loading your order…
      </span>
    </div>
  );
}

// ─── Format helpers ────────────────────────────────────────────────────────────
function formatCurrency(amount) {
  if (!amount) return '₹0.00';
  return new Intl.NumberFormat('en-IN', {
    style:                'currency',
    currency:             'INR',
    minimumFractionDigits: 2,
  }).format(parseFloat(amount));
}

// ─── Main Component ────────────────────────────────────────────────────────────
const OrderSuccessPopup = ({ orderNumber, onClose }) => {
  const navigate = useNavigate();
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [sparkle,      setSparkle]      = useState(false);
  const [visible,      setVisible]      = useState(false);

  useEffect(() => { injectStyles(); }, []);

  useEffect(() => {
    if (orderNumber) fetchOrderDetails(orderNumber);
    else { setError('Order ID not found'); setLoading(false); }
  }, [orderNumber]);

  useEffect(() => {
    if (!loading && !error) {
      setTimeout(() => setVisible(true),  80);
      setTimeout(() => setSparkle(true), 950);
    }
  }, [loading, error]);

  const fetchOrderDetails = async (orderNum) => {
    try {
      setLoading(true);
      const response = await getOrderDetails(orderNum);
      if (response.data?.success && response.data?.data) {
        setOrderDetails(response.data.data);
      } else {
        setError(response.data?.message || 'Failed to fetch order details');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to load order details');
    } finally {
      setLoading(false);
    }
  };

  const handleViewOrder = () => {
    onClose();
    navigate(`/order-success/${orderNumber}`);
  };

  const overlayStyle = {
    position:        'fixed',
    inset:           0,
    background:      THEME.navyGhost,
    backdropFilter:  'blur(4px)',
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'center',
    zIndex:          1400,
    padding:         16,
  };

  const cardStyle = {
    position:        'relative',
    width:           '100%',
    maxWidth:        420,
    maxHeight:       '90vh',
    overflowY:       'auto',
    borderRadius:    24,
    background:      THEME.white,
    boxShadow:       THEME.shadow,
    fontFamily:      "'DM Sans', sans-serif",
    animation:       visible ? 'osp-popIn 0.55s cubic-bezier(.34,1.56,.64,1) both' : 'none',
    opacity:         visible ? 1 : 0,
    scrollbarWidth:  'none',
  };

  if (loading) {
    return (
      <div style={overlayStyle}>
        <div style={{ ...cardStyle, animation: 'none', opacity: 1 }}>
          <LoadingState />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={overlayStyle}>
        <div style={{ ...cardStyle, animation: 'none', opacity: 1, padding: 24 }}>
          <button onClick={onClose} style={closeBtn} aria-label="Close">✕</button>
          <div style={{
            background:   '#fff5f5',
            border:       '1px solid #fca5a5',
            borderRadius: 12,
            padding:      16,
            color:        '#b91c1c',
            fontSize:     14,
            marginTop:    8,
          }}>
            {error}
          </div>
        </div>
      </div>
    );
  }

  if (!orderDetails) return null;

  const vendorOrder  = orderDetails.vendorOrders?.[0];
  const items        = vendorOrder?.items || [];
  const visibleItems = items.slice(0, 2);
  const extraCount   = items.length - 2;

  return (
    <div style={overlayStyle}>
      <SparkleCanvas trigger={sparkle} />

      <div style={cardStyle}>
        <button onClick={onClose} style={closeBtn} aria-label="Close">✕</button>

        <div style={{
          background:     `linear-gradient(135deg, ${THEME.navyStrong} 0%, ${THEME.navy} 35%, ${THEME.navyMid} 100%)`,
          padding:        '32px 24px 36px',
          textAlign:      'center',
          position:       'relative',
          overflow:       'hidden',
          borderRadius:   '24px 24px 0 0',
        }}>
          <span style={{ ...ring, width: 220, height: 220, top: -70, left: -50 }} />
          <span style={{ ...ring, width: 160, height: 160, bottom: -60, right: -30, opacity: 0.08 }} />

          <AnimatedCheck visible={visible} />

          <p style={{
            color:         alpha(THEME.white, 0.85),
            fontSize:      11,
            letterSpacing: '1.2px',
            textTransform: 'uppercase',
            fontWeight:    600,
            margin:        '0 0 4px',
            animation:     visible ? 'osp-slideUp 0.4s ease 0.9s both' : 'none',
          }}>
            Payment Successful
          </p>

          <h2 style={{
            color:      THEME.white,
            fontSize:   26,
            fontWeight: 400,
            margin:     '0 0 14px',
            fontFamily: "'DM Serif Display', serif",
            animation:  visible ? 'osp-slideUp 0.4s ease 1.0s both' : 'none',
          }}>
            Order Placed!
          </h2>

          <span style={{
            display:       'inline-block',
            background:    alpha(THEME.white, 0.18),
            border:        `1px solid ${alpha(THEME.white, 0.3)}`,
            color:         THEME.white,
            fontSize:      12,
            fontWeight:    600,
            padding:       '5px 16px',
            borderRadius:  20,
            letterSpacing: '0.4px',
            animation:     visible ? 'osp-badgePop 0.4s ease 1.1s both' : 'none',
          }}>
            Order #{orderDetails.orderNumber || orderNumber}
          </span>

          <div style={{
            position:           'absolute',
            inset:              0,
            background:         `linear-gradient(90deg, transparent 0%, ${alpha(THEME.white, 0.08)} 50%, transparent 100%)`,
            backgroundSize:     '400px 100%',
            animation:          'osp-shimmer 2.4s linear infinite',
            pointerEvents:      'none',
          }} />
        </div>

        <div style={{ padding: '20px 20px 0' }}>
          <SectionLabel icon="🧾" text="Order Summary" delay="1.1s" visible={visible} />

          <div style={{
            border:       `1px solid ${THEME.border}`,
            borderRadius: 14,
            overflow:     'hidden',
            marginBottom: 10,
          }}>
            {visibleItems.map((item, i) => (
              <div
                key={item.id || i}
                style={{
                  display:       'flex',
                  alignItems:    'center',
                  gap:           12,
                  padding:       '11px 14px',
                  borderBottom:  i < visibleItems.length - 1 ? `1px solid ${THEME.border}` : 'none',
                  animation:     visible ? `osp-slideUp 0.35s ease ${1.15 + i * 0.1}s both` : 'none',
                  opacity:       visible ? undefined : 0,
                }}
              >
                <div style={{
                  width:          40,
                  height:         40,
                  borderRadius:   10,
                  background:     THEME.goldFaint,
                  border:         `1px solid ${THEME.border}`,
                  flexShrink:     0,
                  display:        'flex',
                  alignItems:     'center',
                  justifyContent: 'center',
                  overflow:       'hidden',
                }}>
                  {item.images?.[0]
                    ? <img src={item.images[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <span style={{ fontSize: 18 }}>🛍️</span>
                  }
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{
                    fontSize:     13,
                    fontWeight:   600,
                    color:        THEME.textPrimary,
                    margin:       0,
                    whiteSpace:   'nowrap',
                    overflow:     'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {item.productName}
                  </p>
                  <p style={{ fontSize: 12, color: THEME.textMuted, margin: 0 }}>
                    Qty: {item.quantity}
                  </p>
                </div>

                <p style={{
                  fontSize:   13,
                  fontWeight: 600,
                  color:      THEME.goldDeep,
                  margin:     0,
                  flexShrink: 0,
                }}>
                  {formatCurrency(item.totalPrice)}
                </p>
              </div>
            ))}
          </div>

          {extraCount > 0 && (
            <p style={{
              fontSize: 12,
              color:    THEME.goldDark,
              fontWeight: 600,
              margin:   '0 0 12px',
              cursor:   'pointer',
            }}>
              +{extraCount} more item{extraCount > 1 ? 's' : ''}
            </p>
          )}

          <div style={{
            background:   THEME.goldFaint,
            border:       `1px solid ${THEME.border}`,
            borderRadius: 14,
            padding:      '12px 16px',
            display:      'flex',
            alignItems:   'center',
            justifyContent: 'space-between',
            marginBottom: 16,
            animation:    visible ? 'osp-slideUp 0.35s ease 1.35s both' : 'none',
            opacity:      visible ? undefined : 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 16 }}>📍</span>
              <span style={{ fontSize: 13, color: THEME.textMuted }}>
                {orderDetails.city}, {orderDetails.state}
              </span>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: 11, color: THEME.textMuted, margin: '0 0 1px', fontWeight: 500 }}>Total Amount</p>
              <p style={{ fontSize: 20, fontWeight: 700, color: THEME.goldDeep, margin: 0, fontFamily: "'DM Serif Display', serif" }}>
                {formatCurrency(orderDetails.totalAmount)}
              </p>
            </div>
          </div>
        </div>

        <div style={{ padding: '0 20px 22px' }}>
          <button
            onClick={handleViewOrder}
            style={{
              width:          '100%',
              padding:        '14px 0',
              background:     `linear-gradient(135deg, ${THEME.goldDark} 0%, ${THEME.gold} 100%)`,
              border:         'none',
              borderRadius:   14,
              color:          THEME.navyStrong,
              fontSize:       15,
              fontWeight:     700,
              fontFamily:     "'DM Sans', sans-serif",
              cursor:         'pointer',
              letterSpacing:  '0.3px',
              boxShadow:      `0 4px 18px ${alpha(THEME.gold, 0.35)}`,
              transition:     'transform 0.15s ease, box-shadow 0.15s ease',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform  = 'translateY(-1px)';
              e.currentTarget.style.boxShadow  = `0 7px 24px ${alpha(THEME.gold, 0.45)}`;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform  = 'translateY(0)';
              e.currentTarget.style.boxShadow  = `0 4px 18px ${alpha(THEME.gold, 0.35)}`;
            }}
          >
            View Full Order Details →
          </button>
        </div>
      </div>
    </div>
  );
};

const closeBtn = {
  position:        'absolute',
  top:             14,
  right:           14,
  zIndex:          10,
  width:           32,
  height:          32,
  borderRadius:    '50%',
  border:          '1.5px solid rgba(255,255,255,0.4)',
  background:      'rgba(255,255,255,0.15)',
  backdropFilter:  'blur(4px)',
  color:           '#fff',
  fontSize:        14,
  fontWeight:      600,
  cursor:          'pointer',
  display:         'flex',
  alignItems:      'center',
  justifyContent:  'center',
  lineHeight:      1,
  padding:         0,
  transition:      'background 0.2s',
};

const ring = {
  position:     'absolute',
  borderRadius: '50%',
  border:       '1.5px solid rgba(255,255,255,0.12)',
  pointerEvents:'none',
};

function SectionLabel({ icon, text, delay, visible }) {
  return (
    <p style={{
      fontSize:      12,
      fontWeight:    600,
      color:         THEME.textMuted,
      textTransform: 'uppercase',
      letterSpacing: '0.9px',
      margin:        '0 0 10px',
      display:       'flex',
      alignItems:    'center',
      gap:           6,
      animation:     visible ? `osp-slideUp 0.35s ease ${delay} both` : 'none',
      opacity:       visible ? undefined : 0,
    }}>
      {icon} {text}
    </p>
  );
}

export default OrderSuccessPopup;